/**
 * Metrics by Client Drilldown Component
 * Phase 9.38 - Dashboard Stat Drilldowns
 *
 * Shows financial metrics aggregated by organization/client
 */
import { useMetricsByClient } from '@/hooks/useDashboardReports';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SortBy = 'outstanding' | 'overdue' | 'ytd' | 'available';

export function MetricsByClient() {
  const { data, isLoading, error } = useMetricsByClient();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortBy>('outstanding');

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4 animate-pulse"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <AlertCircle className="inline mr-2" size={20} />
        Failed to load client metrics data
      </div>
    );
  }

  const clients = data?.data || [];
  const summary = data?.summary || {
    total_available_to_invoice: 0,
    total_outstanding: 0,
    client_count: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Sort clients
  const sortedClients = [...clients].sort((a, b) => {
    if (sortBy === 'outstanding') {
      return b.outstanding - a.outstanding;
    } else if (sortBy === 'overdue') {
      return b.overdue - a.overdue;
    } else if (sortBy === 'ytd') {
      return b.ytd_revenue - a.ytd_revenue;
    } else {
      return b.available_to_invoice - a.available_to_invoice;
    }
  });

  const handleRowClick = (organizationId: string) => {
    if (organizationId) {
      navigate(`/organizations/${organizationId}`);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow" data-testid="metrics-by-client">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Metrics by Client
        </h2>
        <div className="flex gap-2 text-sm">
          <button
            onClick={() => setSortBy('outstanding')}
            className={`px-3 py-1 rounded ${sortBy === 'outstanding' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Outstanding
          </button>
          <button
            onClick={() => setSortBy('overdue')}
            className={`px-3 py-1 rounded ${sortBy === 'overdue' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Overdue
          </button>
          <button
            onClick={() => setSortBy('ytd')}
            className={`px-3 py-1 rounded ${sortBy === 'ytd' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            YTD
          </button>
          <button
            onClick={() => setSortBy('available')}
            className={`px-3 py-1 rounded ${sortBy === 'available' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Available
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Available</div>
          <div className="text-xl font-bold text-sky-600" data-testid="summary-available">
            {formatCurrency(summary.total_available_to_invoice)}
          </div>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Total Outstanding</div>
          <div className="text-xl font-bold text-orange-600" data-testid="summary-outstanding">
            {formatCurrency(summary.total_outstanding)}
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-600">Clients</div>
          <div className="text-xl font-bold text-gray-900" data-testid="summary-clients">
            {summary.client_count}
          </div>
        </div>
      </div>

      {/* Table */}
      {sortedClients.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Projects</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">YTD Revenue</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedClients.map((client) => (
                <tr
                  key={client.organization_id || 'no-org'}
                  className={`hover:bg-gray-50 ${client.organization_id ? 'cursor-pointer' : ''}`}
                  onClick={() => handleRowClick(client.organization_id)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {client.organization_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-center text-gray-600">
                    {client.project_count}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-sky-600">
                    {formatCurrency(client.available_to_invoice)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-orange-600">
                    {formatCurrency(client.outstanding)}
                  </td>
                  <td className={`px-4 py-3 text-sm font-semibold text-right ${client.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {formatCurrency(client.overdue)}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-right text-green-600">
                    {formatCurrency(client.ytd_revenue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No client data found
        </div>
      )}
    </div>
  );
}
